/**
 * Cookie consent for the marketing pixels (Meta, TikTok).
 *
 * The pixels are loaded but muted: index.html revokes the Meta consent right after
 * `fbq('init')`, and TrackingPixels holds the TikTok one. Nothing is transmitted until
 * the consent is granted, so the base code stays where Meta's crawler can find it while
 * no personal data leaves the browser. Meta queues the events it could not send and
 * flushes them on `grant`, so an accepted PageView is not lost.
 *
 * The banner only shows where prior consent is required — EEA, United Kingdom and
 * Switzerland — detected from the browser timezone: no IP lookup, no extra request and
 * no third party. Everywhere else the consent is granted on its own and the visitor
 * never sees a banner.
 *
 * The decision is shared with funded.raisemyfunds.co: same key, same JSON, written both
 * to localStorage and to a cookie on `.raisemyfunds.co`. A visitor who accepted on the
 * landing is not asked again here, and the other way round.
 */

/** Shared with the landing. Rename to ask everyone again, e.g. when a vendor is added. */
const STORAGE_KEY = 'rmf_cookie_consent_v2';

/** Key used before the format was agreed with the landing. Read-only, for the migration. */
const LEGACY_STORAGE_KEY = 'cookieConsent';

const COOKIE_MAX_AGE_DAYS = 180;

export type ConsentStatus = 'granted' | 'denied';

export interface StoredConsent {
  analytics: boolean;
  /** ISO date, kept as the proof of when the choice was made. */
  updatedAt: string;
}

/**
 * The regulated-region decision is made by the inline script of index.html, which runs on
 * every page before React. Keeping the timezone list in a single place avoids two copies
 * drifting apart. Absent flag → regulated, so a missing bootstrap never silences the banner.
 */
export const isRegulatedRegion = (): boolean => {
  if (typeof window === 'undefined') return true;
  return window.__consentRegulatedRegion !== false;
};

/**
 * Whether this host is allowed to talk to the ad platforms at all. Decided by the same
 * inline bootstrap, which keeps the production host list in one place. Missing flag means
 * no bootstrap ran, so nothing is tracked — a stale page never leaks preprod traffic.
 */
export const isTrackingHost = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.__trackingHost === true;
};

const readCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const hit = document.cookie.match(`(^|; )${name}=([^;]*)`);
  return hit ? decodeURIComponent(hit[2]) : null;
};

/** Root domain so the landing and /apply share the value; host-only anywhere else. */
const cookieDomain = (): string =>
  /\.raisemyfunds\.co$/.test(window.location.hostname) ? '; domain=.raisemyfunds.co' : '';

const writeCookie = (name: string, value: string, days: number): void => {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${
    days * 86400
  }${cookieDomain()}; SameSite=Lax${secure}`;
};

const clearCookie = (name: string): void => {
  document.cookie = `${name}=; path=/; max-age=0${cookieDomain()}`;
};

const parseConsent = (raw: string | null): StoredConsent | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredConsent;
    return typeof parsed?.analytics === 'boolean' ? parsed : null;
  } catch {
    return null;
  }
};

/** Cookie first — it is the one the landing can write — then localStorage, then legacy. */
export const readConsent = (): StoredConsent | null => {
  if (typeof window === 'undefined') return null;

  const shared = parseConsent(readCookie(STORAGE_KEY));
  if (shared) return shared;

  try {
    const local = parseConsent(localStorage.getItem(STORAGE_KEY));
    if (local) return local;
  } catch {
    /* private mode */
  }

  try {
    const legacy = JSON.parse(localStorage.getItem(LEGACY_STORAGE_KEY) || 'null');
    if (legacy && legacy.version === 1) {
      return { analytics: legacy.status === 'granted', updatedAt: legacy.date ?? '' };
    }
  } catch {
    /* nothing stored */
  }

  return null;
};

/**
 * Runs `task` once fbevents.js is live.
 *
 * Before that, `window.fbq` is only the stub of the base code and every call piles up in
 * its queue. A `consent` call drained from that queue in the same batch as the initial
 * PageView is not honoured — the PageView stays muted for good. Waiting for the real
 * implementation makes the grant flush the held events, which is the documented behaviour.
 * Gives up after ~5s: on a page without the pixel it simply never arrives.
 */
const whenPixelReady = (task: () => void): void => {
  if (typeof window === 'undefined') return;
  if (window.fbq?.callMethod) {
    task();
    return;
  }
  let tries = 0;
  const timer = window.setInterval(() => {
    tries += 1;
    if (window.fbq?.callMethod || tries > 50) {
      window.clearInterval(timer);
      task();
    }
  }, 100);
};

/** Pushes the status down to whichever pixel is already on the page. */
const applyToPixels = (status: ConsentStatus): void => {
  if (typeof window === 'undefined') return;
  const granted = status === 'granted';

  if (granted) {
    // The landing's click ids were held back while the answer was pending: now that
    // tracking is allowed, write _fbc / _fbp before the held events are flushed.
    try {
      window.__rmfPersistFbAttribution?.();
    } catch {
      /* bootstrap did not run on this page */
    }
  }

  whenPixelReady(() => {
    try {
      window.fbq?.('consent', granted ? 'grant' : 'revoke');
    } catch {
      /* pixel not loaded on this page */
    }
  });

  // TikTok defers its own calls until the SDK is ready, no need to wait.
  try {
    if (granted) window.ttq?.grantConsent?.();
    else window.ttq?.holdConsent?.();
  } catch {
    /* TikTok not loaded on this page */
  }
};

type Listener = () => void;
const listeners = new Set<Listener>();

export const subscribeToConsent = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const notify = () => listeners.forEach((l) => l());

export const setConsent = (status: ConsentStatus): void => {
  const payload: StoredConsent = {
    analytics: status === 'granted',
    updatedAt: new Date().toISOString(),
  };
  const raw = JSON.stringify(payload);

  // Cookie so the landing sees it, localStorage so it survives a cookie purge here.
  try {
    writeCookie(STORAGE_KEY, raw, COOKIE_MAX_AGE_DAYS);
  } catch {
    /* cookies blocked */
  }
  try {
    localStorage.setItem(STORAGE_KEY, raw);
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    /* private mode: the choice holds for this page only */
  }

  applyToPixels(status);
  notify();
};

/** Withdrawing must be as easy as giving: drops the choice and mutes the pixels again. */
export const resetConsent = (): void => {
  try {
    clearCookie(STORAGE_KEY);
  } catch {
    /* cookies blocked */
  }
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
  applyToPixels('denied');
  notify();
};

/**
 * Replays the current decision onto the TikTok SDK, which loads long after the page.
 *
 * Meta is deliberately left alone here: the inline bootstrap of index.html already put it
 * in the right state, and fbevents counts its consent calls — a redundant `revoke` on
 * mount would need a second `grant` before anything is sent again.
 */
export const syncTikTokConsent = (): void => {
  if (!isTrackingHost()) return;
  const stored = readConsent();
  const granted = stored ? stored.analytics : !isRegulatedRegion();
  try {
    if (granted) window.ttq?.grantConsent?.();
    else window.ttq?.holdConsent?.();
  } catch {
    /* TikTok not loaded on this page */
  }
};

/**
 * What the pixel is allowed to do right now, mirroring the decision the inline bootstrap
 * of index.html makes. Used by the React path, which owns the pixel when /apply is
 * reached through a client-side navigation and the bootstrap never ran for it.
 *
 * - `denied` the script must not even be fetched
 * - `muted`  load it, but revoke until the visitor answers
 * - `open`   nothing to ask, or already accepted
 */
export type PixelGate = 'denied' | 'muted' | 'open';

export const pixelGate = (): PixelGate => {
  if (!isTrackingHost()) return 'denied';
  const stored = readConsent();
  if (stored) return stored.analytics ? 'open' : 'denied';
  return isRegulatedRegion() ? 'muted' : 'open';
};

/** True when the visitor still has to be asked. Nothing to ask where nothing is tracked. */
export const needsConsentBanner = (): boolean =>
  isTrackingHost() && !readConsent() && isRegulatedRegion();
